import { SpendPickers } from '../../budget/components/SpendPickers';
import { useMemo, useState } from 'react';
import { reconcileTotal, sumEstimates } from '@tahi/core';
import { buy, undoBuy } from '../../../data/commands/shopping';
import { useCommandEnv, useUsable } from '../../../data/hooks';
import type { ShoppingItem } from '../../../data/repos/shopping';
import { Button, Sheet, showToast } from '../../../ui';
import { Switch } from '../../../ui/Switch';
import { minorToInput, parsePesoInput } from '../../budget/format';
import { useToday } from '../../budget/hooks';
import { copy, errorText } from '../copy';
import { quantityText } from '../format';
import { INPUT } from '../styles';

/** The store the ticked items share, if they name one. */
function commonStore(items: readonly ShoppingItem[]): string {
  const stores = new Set(items.map((i) => i.store).filter((s): s is string => !!s));
  return stores.size === 1 ? [...stores][0]! : '';
}

/**
 * "How much?" (spec §6): the estimates summed and filled in, one total = one expense. Optional
 * "Price each item"; when every item has a price the total is their sum, and it never stays below
 * the prices entered.
 */
export function BuySheet({
  items,
  onClose,
  onBought,
}: {
  items: ShoppingItem[];
  onClose(): void;
  onBought(): void;
}) {
  const env = useCommandEnv();
  const usable = useUsable();
  const estimate = sumEstimates(items);
  const [total, setTotal] = useState(estimate > 0 ? minorToInput(estimate) : '');
  const [store, setStore] = useState(() => commonStore(items));
  const [priceEach, setPriceEach] = useState(false);
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [accountId, setAccountId] = useState<string | null>(null);
  // v0.7.6: a credit card picked under "From" ('card:<id>'): the trip is a card purchase.
  const [method, setMethod] = useState<string | null>(null);
  const today = useToday();
  const ids = useMemo(() => items.map((i) => i.id), [items]);

  const priceMinor = (text: string | undefined) =>
    text && text.trim() !== '' ? parsePesoInput(text) : null;
  const pricesMinor = (map: Record<string, string>) =>
    Object.fromEntries(ids.map((id) => [id, priceMinor(map[id])]));
  const totalMinor = parsePesoInput(total);

  function setPrice(id: string, text: string) {
    setError(null);
    const next = { ...prices, [id]: text };
    setPrices(next);
    const reconciled = reconcileTotal(totalMinor, ids, pricesMinor(next));
    if (reconciled !== null && reconciled !== totalMinor) setTotal(minorToInput(reconciled));
  }

  async function submit() {
    if (totalMinor === null) {
      setError(errorText('amount_required'));
      return;
    }
    if (
      priceEach &&
      ids.some((id) => (prices[id] ?? '').trim() !== '' && priceMinor(prices[id]) === null)
    ) {
      setError(errorText('bad_price'));
      return;
    }
    setBusy(true);
    try {
      const r = await buy(env, {
        itemIds: ids,
        totalMinor,
        ...(priceEach ? { prices: pricesMinor(prices) } : {}),
        store,
        ...(method ? { method } : accountId ? { accountId } : {}),
      });
      if (!r.ok) {
        setError(errorText(r.error));
        return;
      }
      showToast({
        message: copy.bought(items.length, totalMinor),
        action: { label: copy.undo, onPress: () => void undoBuy(env, r.tripId) },
      });
      onBought();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()} title={copy.howMuch}>
      <div className="flex flex-col gap-4">
        <ul className="flex flex-col gap-1 text-sm">
          {items.map((i) => (
            <li key={i.id} className="flex min-h-11 items-center justify-between gap-3">
              <span className="min-w-0 truncate text-fg">
                {i.name}
                {i.quantity != null || i.unit ? (
                  <span className="text-muted"> · {quantityText(i.quantity, i.unit)}</span>
                ) : null}
              </span>
              {priceEach ? (
                <span className="relative flex w-32 shrink-0 items-center">
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute left-3 font-mono text-muted"
                  >
                    ₱
                  </span>
                  <input
                    aria-label={copy.priceFor(i.name)}
                    inputMode="decimal"
                    value={prices[i.id] ?? ''}
                    placeholder={i.estimateMinor ? minorToInput(i.estimateMinor) : ''}
                    onChange={(e) => setPrice(i.id, e.target.value)}
                    className={`${INPUT} w-full pl-7 text-right font-mono tabular-nums`}
                  />
                </span>
              ) : i.estimateMinor ? (
                <span className="shrink-0 font-mono tabular-nums text-muted">
                  {minorToInput(i.estimateMinor)}
                </span>
              ) : null}
            </li>
          ))}
        </ul>

        <label className="flex flex-col gap-1 text-sm text-muted">
          {copy.totalLabel}
          <span className="relative flex items-center">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-4 font-mono text-[28px] text-muted"
            >
              ₱
            </span>
            <input
              aria-label={copy.totalLabel}
              inputMode="decimal"
              value={total}
              onChange={(e) => {
                setError(null);
                setTotal(e.target.value);
              }}
              className={`${INPUT} min-h-14 w-full pl-10 font-mono text-[28px] tabular-nums`}
            />
          </span>
          {estimate > 0 ? <span className="text-xs">{copy.estimatesNote(estimate)}</span> : null}
        </label>

        <label className="flex flex-col gap-1 text-sm text-muted">
          {copy.store}
          <input
            value={store}
            maxLength={60}
            onChange={(e) => setStore(e.target.value)}
            className={INPUT}
          />
        </label>

        {usable('spending') && usable('budget') ? (
          <SpendPickers
            withFund={false}
            fundId={null}
            onFund={() => undefined}
            accountId={accountId}
            onAccount={setAccountId}
            card={{ method, onMethod: setMethod, date: today }}
          />
        ) : null}

        {items.length > 1 ? (
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-3">
              <span aria-hidden="true" className="text-fg">
                {copy.priceEach}
              </span>
              <Switch label={copy.priceEach} checked={priceEach} onCheckedChange={setPriceEach} />
            </div>
            {priceEach ? <p className="text-xs text-muted">{copy.priceEachHint}</p> : null}
          </div>
        ) : null}

        <p className="text-sm text-muted">
          {usable('spending') ? copy.logsExpense : copy.noSpending}
        </p>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <Button
          variant="primary"
          disabled={busy || totalMinor === null}
          onClick={() => void submit()}
          className="min-h-12 w-full"
        >
          {copy.boughtCta(totalMinor)}
        </Button>
      </div>
    </Sheet>
  );
}
